<template>
  <div v-if="!hidden" class="my-4" :class="fieldClass || 'w-full'">
    <div class="flex">
      <label v-show="!hidden" :class="required ? 'required' : ''">
        {{ label }}
        <span v-if="!required && !disabled" class="optional">{{this.language.optional[language_id]}}</span>
      </label>
      <div v-if="requiredMessage" class="text-red-500 ml-auto">
        {{this.language.first_time_log[language_id]}}
      </div>
    </div>
    <!-- Text -->
    <input
      v-show="!hidden"
      v-if="type === 'text'"
      class="w-full"
      :name="field"
      type="text"
      :disabled="disabled"
      :maxlength="maxlength"
      v-model="fieldValue"
      @keyup="$emit('update', fieldValue)"
    />
    <!-- Zip Code digits only, maxlength 5 -->
    <input
      v-if="type === 'zip'"
      class="w-full"
      :name="field"
      type="text"
      inputmode="numeric"
      :maxlength="maxlength"
      :disabled="disabled"
      v-model="fieldValue"
      @keypress="!/^\d$/.test($event.key) ? $event.preventDefault() : null"
      @keyup="$emit('update', fieldValue)"
    />
    <!-- Select -->
    <select
      v-if="type === 'select'"
      class="w-full bg-white"
      :name="field"
      :disabled="disabled"
      v-model="fieldValue"
      @change="$emit('update', fieldValue)"
    >
      <option value=""></option>
      <option
        v-for="option in options"
        :value="option.id"
        :key="option.id"
      >
        {{ option.name }}
      </option>
    </select>
    <!-- Multiselect -->
    <multiselect
      v-if="type === 'multiselect' && options.length"
      class="w-full bg-white"
      v-model="fieldValue"
      :multiple="true"
      placeholder=""
      label="name"
      track-by="name"
      :searchable="false"
      :close-on-select="false"
      :clear-on-select="false"
      :options="options"
      @input="$emit('update', fieldValue)"
    >
    </multiselect>
    <!-- Boolean -->
    <select
      v-if="type === 'boolean'"
      class="w-full bg-white"
      :name="field"
      :disabled="disabled"
      v-model="fieldValue"
      @change="$emit('update', fieldValue)"
    >
      <option value=""></option>
      <option value="false">No</option>
      <option value="true">{{this.language.yes[language_id]}}</option>
    </select>
    <!-- Date -->
    <input
      v-if="type === 'date' && !isMobile"
      class="ios bg-white w-full"
      :name="field"
      type="date"
      :disabled="disabled"
      v-model="fieldValue"
      @blur="$emit('blur', fieldValue)"
      @change="$emit('update', fieldValue)"
    />
    <!-- Custom Date Picker -->
    <div v-if="type === 'date' && isMobile" class="relative">
      <input
        class="w-full bg-white"
        :name="field"
        type="text"
        :value="displayDate"
        :disabled="disabled"
        readonly
        @click="openDatePicker"
      />
      <!-- Modal -->
      <date-picker-modal
        v-if="showDatePicker"
        :value="fieldValue"
        @close="showDatePicker = false"
        @select="onDateSelect"
        :language_id="language_id"
      />
    </div>
    <!-- Phone -->
    <div class="flex" v-if="type === 'phone'">
      <div class="country-code-picker mr-2">
        <div @click="openOptions" class="selectedValue">
          <div
            class="flag"
            v-bind:class="{
              us: selectedCode.country === 'us',
              nz: selectedCode.country === 'nz',
            }"
          ></div>
          <div class="code">{{ selectedCode.code }}</div>
        </div>
        <div v-if="optionsOpen" class="options">
          <div
            class="option cursor-pointer"
            v-for="option in countryCodeOptions"
            :key="option.code"
            @click="selectOption(option)"
          >
            <div
              class="flag"
              v-bind:class="{
                us: option.country === 'us',
                nz: option.country === 'nz',
              }"
            ></div>
            <div class="code">{{ option.code }}</div>
          </div>
        </div>
      </div>
      <input
        class="w-full"
        :name="field"
        :placeholder="placeholderNumber"
        v-mask="phoneNumberMask"
        type="text"
        :disabled="disabled"
        @change="changePhone"
        v-model="phoneNumber"
      />
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect'
import { getPayerNames, getPayerTypes, getLevelsOfCare, getPrimarySubstances, getRace} from '../../services/integration-profile-service'
import { translations } from '../../translations'
import DatePickerModal from '../DatePickerModal.vue';

export default {
  name: 'FormField',
  components: {
    Multiselect,
    DatePickerModal
  },
  props: [
    'field',
    'label',
    'type',
    'value',
    'required',
    'requiredMessage',
    'disabled',
    'fieldClass',
    'enable',
    'language_id',
    'hidden',
    'entityOptions',
    'maxlength'
  ],
  data() {
    return {
      fieldValue: '',
      options: [],
      optionsOpen: false,
      //language_id: null,
      language: translations(), 
      selectedCode: { code: "+1", country: "us" },
      countryCodeOptions: [
        { code: "+1", country: "us" },
        { code: "+64", country: "nz" },
      ],
      phoneNumber: "",
      placeholderNumber: "(123)-456-7890",
      phoneNumberMask: "(###)-###-####",
      genderOptions: [],
      languageOptions: [{
        id: 1, name: "English"},
       {id: 2, name: "Spanish"}],
      showDatePicker: false
    };
  },
  computed: {
    //check if mobile device
    isMobile() {
      return (
        window.matchMedia('(pointer: coarse)').matches ||
        window.innerWidth <= 768
      )
    },
    displayDate() {
      if (!this.fieldValue) return ''

      const d = new Date(this.fieldValue + 'T00:00:00')

      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      const yyyy = d.getFullYear()

      return `${mm}/${dd}/${yyyy}`
    }
  },
  created() {
    // get select value options
    if (this.type === 'select' || this.type === 'multiselect') {
      this.getOptions();
    }

    //if Timezone is a new Zealand timezone, default the country code to new Zealand's
    if (Intl.DateTimeFormat().resolvedOptions().timeZone == 'Pacific/Chatham' || Intl.DateTimeFormat().resolvedOptions().timeZone == 'Pacific/Auckland' ){
       this.selectedCode = { code: "+64", country: "nz" };
    }
    
  },
  methods: {
    openOptions() {
      this.optionsOpen = true;
    },
    selectOption(option) {
      this.selectedCode = option;
      this.fieldValue = this.selectedCode.code + this.phoneNumber;
      this.optionsOpen = false;
      if (this.selectedCode.country === "us") {
        this.placeholderNumber = "(123)-456-7890";
        this.phoneNumberMask = "(###)-###-####";
      } else {
        this.placeholderNumber = "000000000000000";
        this.phoneNumberMask = "###############";
      }
      this.changePhone()
    },
    getGenderOptions() {
      this.genderOptions = [{ id: 1, name: this.language.male[this.language_id]},
        { id: 2, name: this.language.female[this.language_id]},
        { id: 4, name: this.language.transgender[this.language_id]},
        { id: 5, name: this.language.nonbinary[this.language_id]},
        { id: 3, name: this.language.other[this.language_id]}];
    },
    changePhone() {
      if (this.validPhoneNumber()) {
        this.fieldValue = this.selectedCode.code + this.phoneNumber;
      } else {
        this.fieldValue = '';
      }
      this.fieldValue = '+' + this.fieldValue.replace(/\D/g, '');
      this.$emit('update', this.fieldValue);
    },
    validPhoneNumber() {
      // get all numbers in a string
      var numbers = this.phoneNumber.toString().trim().replace(/\D/g, "");
      switch (this.selectedCode.country) {
        case "us":
          if (numbers.length === 10) {
            return true;
          }
          return false;
        case "nz":
          if (numbers.length) {
            return true;
          }
          return false;
      }
    },
    getOptions() {
      switch (this.field) {
        case 'gender_id':
          this.getGenderOptions();
          // set gender options
          this.options = this.genderOptions;
          break;
        case 'payer_name_id':
          // get payer names
          getPayerNames().then(res => {
            if (res.success) {
              let first = [];
              let data = [];
              this.options = res.data.map(data => ({ id: data.Payer_Name_ID, name: data.Value }));
              for(let option of this.options){
                if(option.name.trim().toLocaleLowerCase() == "wv medicaid-unicare"){
                  first.push(option);
                  this.options = this.options.filter(op => op.name != option.name);
                }

                if(option.name.trim().toLocaleLowerCase() == "unknown" 
                || option.name.trim().toLocaleLowerCase() == "other"){
                  data.push(option)
                  this.options = this.options.filter(op => op.name != option.name);
                }
              }
              this.options = this.options.sort((a, b) => a.name.localeCompare(b.name))
              if(first.length > 0){
                for(let option of first){
                  this.options.unshift(option)
                }
              }
              if(data.length > 0){
                this.options = this.options.concat(data)
              }
            }
          });
          break;
        case 'payer_type_id':
          // get payer types
          getPayerTypes().then(res => {
            if (res.success) {
              this.options = res.data.map(data => ({ id: data.Payer_Type_ID, name: data.Value }));
            }
          });
          break;
        case 'loc':
          // get levels of care
          getLevelsOfCare().then(res => {
            if (res.success) {
              this.options = res.data.map(data => ({ id: data.ID, name: data.Level_Of_Care }));
            }
          });
          break;
        case 'primary_substance_id':
          // get primary substances
          getPrimarySubstances().then(res => {
            if (res.success) {
              this.options = res.data.map(data => ({ id: data.ID, name: data.Substance_Name }));
            }
          });
          break;
        case 'race_id': 
          getRace().then(res => {
            if (res.success) {
              this.options = res.data.map(data => ({ id: data.Race_ID, name: this.language_id == 2? data.Spanish_Name : data.Name }));
              let found_unknown
              let found_other
              let found_decline
              for(let option of this.options){
                if(option.name.trim().toLocaleLowerCase() == "patient declined" || option.name.trim().toLocaleLowerCase() == "el paciente declinó"){
                found_decline = option;
                continue
                }

                if(option.name.trim().toLocaleLowerCase() == "other" || option.name.trim().toLocaleLowerCase() == "otro"){
                found_other = option;
                continue
                }

                if(option.name.trim().toLocaleLowerCase() == "unknown" || option.name.trim().toLocaleLowerCase() == "desconocido"){
                found_unknown = option;
                continue
                }
              }
              let found = []
              if(found_decline) found.push(found_decline.name)
              if(found_unknown) found.push(found_unknown.name)
              if(found_other) found.push(found_other.name)

              this.options = this.options.sort((a, b) => a.name.localeCompare(b.name))
              this.options = this.options.filter(race => !found.includes(race.name));

              if(found_decline) this.options.push(found_decline);
              if(found_unknown) this.options.push(found_unknown);
              if(found_other) this.options.push(found_other);

            }
          });
          break;
        case 'language_id':
          this.options = this.languageOptions;
          break;
        case 'entity_id':
          this.options = this.entityOptions;
          break;
      }
    },
    openDatePicker() {
      if (!this.disabled) {
        this.showDatePicker = true
      }
    },
    onDateSelect(dateString) {
      this.fieldValue = dateString
      this.showDatePicker = false
    }
  }
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style scoped>
label {
  display: block;
  font-size: 13px;
  text-align: left;
  margin-bottom: 8px;
  color: #597495;
  font-weight: 700;
}
label.required::after {
  content: "*";
  color: red;
}
label .optional {
  color: #a0a0a0;
  font-size: 12px;
}
input,
select {
  font-size: 14px;
  font-weight: 400;
  color: #333;
  height: 40px;
  border-radius: 4px;
  padding: 6px 12px;
  line-height: 1.42857143;
  border: 1px solid #ccc;
  -webkit-box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%);
  box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%);
  -webkit-transition: border-color ease-in-out 0.15s,
    -webkit-box-shadow ease-in-out 0.15s;
  -o-transition: border-color ease-in-out 0.15s, box-shadow ease-in-out 0.15s;
  transition: border-color ease-in-out 0.15s, box-shadow ease-in-out 0.15s;
}
input:focus,
select:focus {
  border-color: #66afe9;
  outline: 0;
  -webkit-box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%),
    0 0 8px rgb(102 175 233 / 60%);
  box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%), 0 0 8px rgb(102 175 233 / 60%);
}
input:disabled {
  cursor: not-allowed;
  background-color: rgba(216, 215, 215, 0.8);
}
.us {
  background-image: url("../../assets/images/us_flag.png");
}
.nz {
  background-image: url("../../assets/images/nz_flag.png");
}
.flag {
  display: inline-block;
  height: 12px;
  width: 17px;
}
.code {
  display: inline-block;
}
.country-code-picker {
  position: relative;
  width: 50px;
  height: 40px;
  border-radius: 4px;
  border: 1px solid #ccc;
}
.selectedValue {
  width: 50px;
  height: 100%;
  cursor: pointer;
  padding: 10px 0px;
  text-align: center;
  border-radius: 4px;
}
.options {
  position: absolute;
  background: white;
  border: 1px solid #ccc;
  width: 50px;
  top: -1px;
  left: -1px;
  border-radius: 4px;
  z-index: 2;
}
.option {
  padding: 2px 3px;
}
.ios {
  -webkit-appearance: none;
  -moz-appearance: none;
}
</style>
<style>
/* multiselect override styles */
.multiselect__tags {
  font-size: 14px;
  font-weight: 400;
  color: #333;
  height: 40px;
  border-radius: 4px;
  padding: 6px 12px;
  line-height: 1.42857143;
  border: 1px solid #ccc;
  -webkit-box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%);
  box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%);
  -webkit-transition: border-color ease-in-out 0.15s,
    -webkit-box-shadow ease-in-out 0.15s;
  -o-transition: border-color ease-in-out 0.15s, box-shadow ease-in-out 0.15s;
  transition: border-color ease-in-out 0.15s, box-shadow ease-in-out 0.15s;
}
.multiselect__tags:focus {
  border-color: #66afe9;
  outline: 0;
  -webkit-box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%),
    0 0 8px rgb(102 175 233 / 60%);
  box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%), 0 0 8px rgb(102 175 233 / 60%);
}
.multiselect__tag,
.multiselect__tag-icon:focus,
.multiselect__tag-icon:hover {
  background: #319af1;
}
.multiselect__input {
  display: none;
}
.multiselect__element {
  font-size: 14px;
}
.multiselect__option--highlight,
.multiselect__option--highlight::after {
  background: #319af1;
}
</style>